// §87 unified upload manager – the per-file transport leaf for the `rail` size
// variant. Each picked/dropped file is its OWN POST request (per-file progress +
// per-file error isolation – the §87 transport rule, generalizing intake.ts's
// proven worker). This leaf only moves bytes: the rail cards and the phased
// progress are server-rendered from the ui.upload.* events keyed by
// (tab, instance, slot) – the leaf never renders and never owns state.
//
// Single-shot fields (`inline` / `panel`) do NOT use this leaf – they submit via
// a declarative Datastar @post on the enclosing form. Only the `rail` (and any
// multi-file panel) drives per-file uploads here.

const UPLOAD_CONCURRENCY = 3;

interface UploadResult {
  slot: number;
  ok: boolean;
  docId?: string;
}

// uploadFiles POSTs each file to `url` under form field `field`, at most
// UPLOAD_CONCURRENCY in flight. tab/instance/slot ride the query string (not the
// body) so the server can scope its ui.upload.* progress events before it parses
// the multipart body (the §64 correlation-in-query rule). Returns per-slot
// results (ok + an optional docId the endpoint may echo) in pick order.
async function uploadFiles(
  url: string,
  field: string,
  tabId: string,
  instanceId: string,
  files: File[],
): Promise<UploadResult[]> {
  const results: UploadResult[] = new Array(files.length);
  let next = 0;
  const worker = async (): Promise<void> => {
    while (next < files.length) {
      const slot = next++;
      const body = new FormData();
      body.append(field, files[slot]);
      const q =
        `tab=${encodeURIComponent(tabId)}` +
        `&batch=${encodeURIComponent(instanceId)}` +
        `&slot=${slot}` +
        `&name=${encodeURIComponent(files[slot].name)}`; // §86: rail card label
      try {
        const r = await fetch(`${url}?${q}`, { method: 'POST', body });
        let docId: string | undefined;
        try {
          docId = ((await r.clone().json()) as { docId?: string }).docId;
        } catch {
          /* no JSON body (e.g. a 204) – fine, docId stays undefined */
        }
        results[slot] = { slot, ok: r.ok, docId };
      } catch {
        results[slot] = { slot, ok: false };
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(UPLOAD_CONCURRENCY, files.length) }, worker),
  );
  return results;
}

// railUpload reads the destination off the input's data-* (set once by the
// Uploader templ) so the call site stays declarative, then fires the per-file
// uploads. tabId is passed in from the Datastar `$tabId` signal by the handler
// expression. Fire-and-forget: the server patches the rail cards from the
// ui.upload.* stream as bytes arrive.
function railUpload(input: HTMLInputElement, files: File[], tabId: string): void {
  if (!files.length) return;
  const url = input.dataset.uplUrl ?? '';
  const field = input.dataset.uplField ?? input.name ?? 'file';
  const instance = input.dataset.uplId ?? '';
  const orderUrl = input.dataset.uplOrderUrl ?? '';
  if (!url) return;
  void uploadFiles(url, field, tabId, instance, files).then((results) => {
    if (!orderUrl) return;
    // §86 batch-end pick-order: hand the new documents to the destination in the
    // order they were PICKED (results are indexed by slot = pick order), not the
    // order they happened to finish uploading. Only successful uploads carry a
    // docId. The server appends them after the existing set in this order.
    const order = results
      .filter((r) => r && r.ok && r.docId)
      .map((r) => r.docId as string);
    if (!order.length) return;
    void fetch(orderUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ order }),
    });
  });
}

// __uplPick: the rail input's change handler (a click-picked FileList).
window.__uplPick = (input: HTMLInputElement, tabId: string): void =>
  railUpload(input, Array.from(input.files ?? []), tabId);

// __uplDrop: the rail label's drop handler (a dropped FileList). The label reaches
// its sibling input by id so url/field/instance resolve the same way as a pick.
window.__uplDrop = (evt: DragEvent, input: HTMLInputElement, tabId: string): void =>
  railUpload(input, Array.from(evt.dataTransfer?.files ?? []), tabId);

export {};
